import { createContext, useCallback, useState } from 'react';
import { AccessLevel, ManagedUser, UserCatalog, UserType } from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';
import { errorMessage } from '../../utils/access';

export interface UserFilters {
    bankId?: number | null;
    merchantId?: number | null;
    search?: string;
}

// Datos del formulario de usuario (nivel, dueño, tipo y grupos)
export interface UserForm {
    id?: number;
    username: string;
    name: string;
    lastname: string;
    email: string;
    level: AccessLevel;
    bankId: number | null;
    merchantId: number | null;
    userType: UserType;
    groupIds: number[];
    cashRegister: boolean;
}

type UsersContextProps = {
    users: ManagedUser[];
    totalPage: number;
    isLoading: boolean;
    catalog: UserCatalog | null;
    suggestions: string[];
    loadUsers: (level: AccessLevel, page: number, filters: UserFilters) => Promise<void>;
    loadCatalog: () => Promise<UserCatalog | null>;
    saveUser: (user: UserForm) => Promise<string>;
    deleteUser: (id: number) => Promise<string>;
    resetPassword: (id: number) => Promise<{ email?: string; error?: string }>;
    clearSuggestions: () => void;
}

export const UsersContext = createContext({} as UsersContextProps);

const PAGE_SIZE = 25;

// Usuarios por nivel: plataforma, banco y comercio (el API limita el alcance de quien consulta)
export const UsersProvider = ({ children }: any) => {

    const [users, setUsers] = useState<ManagedUser[]>([]);
    const [totalPage, setTotalPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [catalog, setCatalog] = useState<UserCatalog | null>(null);
    const [suggestions, setSuggestions] = useState<string[]>([]);

    const loadUsers = useCallback(async (level: AccessLevel, page: number, filters: UserFilters) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.bankId) params.set('bankId', String(filters.bankId));
            if (filters.merchantId) params.set('merchantId', String(filters.merchantId));
            if (filters.search?.trim()) params.set('search', filters.search.trim());
            const resp = await topApi.get<{ users: ManagedUser[]; pages: number }>(`/user/list/${level}/${page}/${PAGE_SIZE}?${params.toString()}`);
            setUsers(resp.data.users ?? []);
            setTotalPage(resp.data.pages ?? 1);
        } catch {
            setUsers([]);
            setTotalPage(1);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadCatalog = useCallback(async () => {
        try {
            const resp = await topApi.get<UserCatalog>('/user/catalog');
            setCatalog(resp.data);
            return resp.data;
        } catch {
            return null;
        }
    }, []);

    const saveUser = async (user: UserForm) => {
        try {
            if (user.id) {
                await topApi.put(`/user/${user.id}`, { user });
                return 'success';
            }
            const resp = await topApi.post<{ error?: string; suggestions?: string[] }>('/user/', { user });
            if (resp.data.error) {
                setSuggestions(resp.data.suggestions ?? []);
                return resp.data.error;
            }
            return 'success';
        } catch (e) {
            return errorMessage(e);
        }
    }

    const deleteUser = async (id: number) => {
        try {
            await topApi.delete(`/user/${id}`);
            setUsers(prev => prev.filter(user => user.id !== id));
            return 'success';
        } catch (e) {
            return errorMessage(e);
        }
    }

    // Contraseña temporal nueva, enviada por correo (el API la cambia también en Loyalty)
    const resetPassword = async (id: number) => {
        try {
            const resp = await topApi.post<{ email: string }>(`/user/${id}/reset-password`);
            return { email: resp.data.email };
        } catch (e) {
            return { error: errorMessage(e) };
        }
    }

    const clearSuggestions = () => setSuggestions([]);

    return (
        <UsersContext.Provider value={{ users, totalPage, isLoading, catalog, suggestions, loadUsers, loadCatalog, saveUser, deleteUser, resetPassword, clearSuggestions }}>
            {children}
        </UsersContext.Provider>
    );
}
